import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TableContainer,
    Paper,
    Button,
    useTheme,
    CircularProgress,
    FormGroup,
    FormControlLabel,
    Switch,
} from '@mui/material'
import { Settings, Trash, UserIcon, PersonIcon, Edit } from '@/shared/ui/Icons'
import { memo, useState, ChangeEvent } from 'react'
import { useSharedStyles } from '@/pages/Settings/useSharedStyles'
import {
    DevicesResultType,
    DeviceType,
} from '@/types/settings/DevicesResultType'
import { useTranslation } from 'react-i18next'
import { getFromLocalStorage } from '@/shared/localStorage/localStorageRepository'

interface Props {
    openDeviceDeactivateModal: (deviceId: string, alias?: string | null) => void
    openSetDeviceAliasModal: (deviceId: string, alias?: string | null) => void
    devices: DevicesResultType
}

const DevicesSection = memo(
    ({
        openDeviceDeactivateModal,
        openSetDeviceAliasModal,
        devices,
    }: Props) => {
        const [showDeactivateDevice, setShowDeactivateDevice] =
            useState<boolean>(false)
        const { tableRowStyle, tableCellStyle } = useSharedStyles()
        const theme = useTheme()
        const { t: translate } = useTranslation()
        const currentDeviceId = getFromLocalStorage('device_id')

        const styles = {
            tableContainer: {
                backgroundColor: '#1E1E1E',
                boxShadow: 3,
                borderRadius: theme.shape.borderRadius,
                marginY: '1rem',
            },
            tableCell: (isActive: boolean) => ({
                color: isActive
                    ? theme.palette.white
                    : theme.palette.custom.placeholder,
            }),
        }

        const renderTableHead = () => (
            <TableHead>
                <TableRow
                    sx={{ backgroundColor: theme.palette.custom.midGray }}>
                    {[
                        translate('settingsPageTableHeadID'),
                        translate('settingsPageTableHeadYourName'),
                        translate('settingsPageTableHeadPlatform'),
                        translate('settingsPageTableHeadFirstUse'),
                        translate('settingsPageTableHeadLastUse'),
                        '',
                    ].map((header: string, index: number) => (
                        <TableCell
                            key={index}
                            align={index === 5 ? 'center' : 'right'}
                            sx={tableCellStyle}>
                            {index === 5 ? <Settings /> : header}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        )

        const renderDeviceRow = (device: DeviceType) => {
            const isActive = device.status === '1'

            return (
                <>
                    {(!showDeactivateDevice && isActive) ||
                    showDeactivateDevice ? (
                        <TableRow key={device.device_id} sx={tableRowStyle}>
                            <TableCell
                                component="th"
                                scope="row"
                                sx={styles.tableCell(isActive)}>
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}>
                                    {currentDeviceId === device.device_id ? (
                                        <PersonIcon />
                                    ) : (
                                        <UserIcon />
                                    )}
                                    {device.device_id}
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={styles.tableCell(isActive)}>
                                <Typography> {device.alias || '-'}</Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={styles.tableCell(isActive)}>
                                <Typography>{device.platform}</Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={styles.tableCell(isActive)}>
                                <Typography>{device.first_seen}</Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={styles.tableCell(isActive)}>
                                <Typography>{device.last_seen}</Typography>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={styles.tableCell(isActive)}>
                                {isActive && (
                                    <>
                                        <Button
                                            onClick={() =>
                                                openSetDeviceAliasModal(
                                                    device.device_id,
                                                    device.alias
                                                )
                                            }>
                                            <Edit
                                                fill={
                                                    theme.palette.primary.main
                                                }
                                                height="30px"
                                                width="30px"
                                            />
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                openDeviceDeactivateModal(
                                                    device.device_id,
                                                    device.alias
                                                )
                                            }>
                                            <Trash
                                                fill={
                                                    theme.palette.primary.main
                                                }
                                                height="30px"
                                                width="30px"
                                            />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ) : (
                        ''
                    )}
                </>
            )
        }

        const renderTableBody = () => (
            <TableBody>
                {devices.map((device: DeviceType) => renderDeviceRow(device))}
            </TableBody>
        )

        return (
            <>
                {devices.length ? (
                    <>
                        <FormGroup>
                            <FormControlLabel
                                sx={{
                                    marginRight: 0,
                                    display: 'inline',
                                    textAlign: 'right',
                                }}
                                control={
                                    <Switch
                                        name="showDeactivate"
                                        onChange={(
                                            event: ChangeEvent<HTMLInputElement>
                                        ) =>
                                            setShowDeactivateDevice(
                                                event.target.checked
                                            )
                                        }
                                    />
                                }
                                label={translate('showTurnOffDevices')}
                            />
                        </FormGroup>
                        <TableContainer
                            component={Paper}
                            sx={styles.tableContainer}>
                            <Table
                                sx={{ minWidth: 650 }}
                                aria-label="devices table">
                                {renderTableHead()}
                                {renderTableBody()}
                            </Table>
                        </TableContainer>
                    </>
                ) : (
                    <Box sx={{ marginY: 'auto', textAlign: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
            </>
        )
    }
)

DevicesSection.displayName = 'DevicesSection'

export { DevicesSection }
